import { AxiosError } from "axios";
import { ethers } from "ethers";
import { useCallback, useContext } from "react";
import {
  GenericErrorResponse,
  PostWithdraw200Response,
  PostWithdrawPayload,
} from "../../../codegen-api";
import {
  AllCollaterals,
  CONTRACT_ADDRESSES,
  DepositWithdrawCollaterals,
  MULTICHAIN_CONTRACT_ADDRESSES,
  getSocketConnectorAddress,
} from "../../../constants/addresses";
import { AuthContext } from "../../../contexts/AuthContext";
import { ChainIdEnum } from "../../../enums/chain";
import { authApi } from "../../../services/api/apiFetcher";
import { getDomainData } from "../../../utils/signing";
import useDepositERC20OrETH from "../../contracts/useCollateralAssetOrETH";
import { IWallet } from "../../wallet/useWallet";
import useSocketApi from "../socketApi/useSocketApi";

const withdrawType = [
  { name: "collateral", type: "address" },
  { name: "to", type: "address" },
  { name: "amount", type: "uint256" },
  { name: "salt", type: "uint256" },
  { name: "data", type: "bytes32" },
];

const transferType = [
  { name: "collateral", type: "address" },
  { name: "to", type: "address" },
  { name: "amount", type: "uint256" },
  { name: "salt", type: "uint256" },
];

interface IWithdrawMessage {
  collateral: string;
  to: string;
  amount: string;
  salt: string;
  data: string;

  // For socket withdrawals
  socket_fees?: string;
  socket_msg_gas_limit?: string;
  socket_connector?: string;
}

interface ITransferMessage {
  collateral: string;
  to: string;
  amount: string;
  salt: string;
  label?: string;
  from?: string;
}

type IWithdrawERC20MultichainResponse =
  | {
      // Limit in asset terms, eg. 100 USDC
      insufficientLimit: string;
      response: undefined;
    }
  | {
      insufficientLimit: undefined;
      response: PostWithdraw200Response;
    };

export const useWithdrawalTransfer = (
  wallet: IWallet,
  asset: AllCollaterals,
  destinationChainId?: ChainIdEnum
) => {
  const { account, provider, chainId: connectedChainId } = wallet;
  const { apiKey, apiSecret } = useContext(AuthContext);

  const chainId = destinationChainId || connectedChainId;
  const { withdrawalFees, vaultLimitSWR } = useSocketApi(chainId, asset);
  const depositAsset = useDepositERC20OrETH(wallet, asset);

  const { l1Contract, l2Address, decimals } = depositAsset;

  const createWithdrawal = useCallback(
    async (amountStr: string): Promise<IWithdrawERC20MultichainResponse> => {
      if (
        !account ||
        !provider ||
        !l1Contract ||
        (!provider.provider.sendAsync && !provider.provider.request) ||
        !chainId ||
        !l2Address
      ) {
        throw Error("Unauthenticated");
      }

      // if socket connector address, its using socket withdrawal
      const socketConnectorAddress = getSocketConnectorAddress(
        asset as DepositWithdrawCollaterals,
        "withdrawal",
        chainId
      );

      // Get signing keys, and sign message
      const salt = Math.floor(Math.random() * 100000);
      const amount = ethers.utils.parseUnits(amountStr, decimals);
      const to = socketConnectorAddress
        ? MULTICHAIN_CONTRACT_ADDRESSES[
            chainId as keyof typeof MULTICHAIN_CONTRACT_ADDRESSES
          ].l2WithdrawProxy
        : CONTRACT_ADDRESSES[chainId as ChainIdEnum]?.l2WithdrawProxy ??
          account;

      const withdrawMessage: IWithdrawMessage = {
        collateral: l2Address,
        to,
        amount: amount.toString(),
        salt: salt.toString(),
        data: ethers.utils.keccak256("0x"),
      };

      // Sign message and post payload
      const domainData = getDomainData(chainId);
      const providerSigner = provider.getSigner();

      // Add socket params if its a withdrawal from a supported socket chainId
      let socket_fees: string | undefined;
      let socket_msg_gas_limit: string | undefined;
      let socket_connector: string | undefined;

      if (socketConnectorAddress) {
        // Check bridge capacity using deposit connector address. Returns early if not enough limit
        const limitData = await vaultLimitSWR.mutate();
        const limit = limitData?.find(
          (l) => l.socket_connector === socketConnectorAddress
        )?.capacity;
        if (limit && Number(limit) < Number(amountStr)) {
          return {
            insufficientLimit: limit,
            response: undefined,
          };
        }

        if (!withdrawalFees) {
          throw Error("No withdrawal fees");
        }

        socket_fees = withdrawalFees.toString();
        socket_msg_gas_limit =
          chainId === ChainIdEnum.ARBITRUM ||
          chainId === ChainIdEnum.ARBITRUM_TESTNET
            ? "2000000"
            : "500000";
        socket_connector = socketConnectorAddress;

        const encodedSocketData = ethers.utils.defaultAbiCoder.encode(
          ["uint256", "uint256", "address"],
          [socket_fees, socket_msg_gas_limit, socket_connector]
        );

        withdrawMessage.data = ethers.utils.keccak256(encodedSocketData);
      }

      const withdrawSignature = await providerSigner._signTypedData(
        domainData,
        { Withdraw: withdrawType },
        withdrawMessage
      );

      const withdrawPayload: PostWithdrawPayload = {
        account,
        signature: ethers.utils.joinSignature(
          ethers.utils.splitSignature(withdrawSignature)
        ),
        collateral: withdrawMessage.collateral,
        to: withdrawMessage.to,
        amount: withdrawMessage.amount,
        salt: withdrawMessage.salt,
        socket_fees,
        socket_msg_gas_limit,
        socket_connector,
      };

      const fetcher = authApi();
      try {
        const response = (await (await fetcher.postWithdraw(withdrawPayload))())
          .data;
        return {
          insufficientLimit: undefined,
          response,
        };
      } catch (error: any) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error || "Error withdrawing"
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      account,
      provider,
      l1Contract,
      chainId,
      l2Address,
      asset,
      decimals,
      vaultLimitSWR.mutate,
      withdrawalFees,
    ]
  );

  const createTransfer = useCallback(
    async (
      amountStr: string,
      toAccount: string,
      onMessageSigned: () => void,
      label?: string
    ) => {
      if (
        !account ||
        !provider ||
        !provider.provider.sendAsync ||
        !chainId ||
        !l2Address
      ) {
        throw Error("Unauthenticated");
      }

      const fetcher = authApi(apiKey, apiSecret);

      // Get signing keys, and sign message
      const salt = Math.floor(Math.random() * 100000);

      const amount = ethers.utils.parseUnits(amountStr, decimals);
      const to = toAccount;

      const transferMessage: ITransferMessage = {
        collateral: l2Address,
        to,
        amount: amount.toString(),
        salt: salt.toString(),
      };

      const domainData = getDomainData(chainId);

      const providerSigner = provider.getSigner();
      /* eslint no-underscore-dangle: 0 */
      const transferSignature = await providerSigner._signTypedData(
        domainData,
        {
          Transfer: transferType,
        },
        transferMessage
      );
      onMessageSigned();

      const postBody = {
        account,
        collateral: l2Address,
        to,
        amount: amount.toString(),
        salt: String(salt),
        signature: ethers.utils.joinSignature(
          ethers.utils.splitSignature(transferSignature)
        ),
        label: label ?? "",
      };

      try {
        const response = (await (await fetcher.postTransfer(postBody))()).data;
        return response;
      } catch (error: any) {
        const genericResponseAxiosError =
          error as AxiosError<GenericErrorResponse>;
        throw Error(
          genericResponseAxiosError.response?.data?.error || "Error withdrawing"
        );
      }
    },
    [account, provider, chainId, l2Address, apiKey, apiSecret, decimals]
  );

  return {
    createWithdrawal,
    createTransfer,
    depositAsset,
  };
};
